if (!customElements.get('aura-tax-document')) {
  class AuraTaxDocument extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.updateUrl = this.dataset.updateUrl || '/cart/update.js';
      this.checkoutButton = document.getElementById('checkout');
      this.radios = [...this.querySelectorAll('input[name="aura-document-type"]')];
      this.invoicePanel = this.querySelector('.aura-tax-document__invoice');
      this.invoiceOption = this.querySelector('[data-invoice-option]');
      this.saveError = this.querySelector('.aura-tax-document__save-error');
      this.fields = {
        businessName: this.querySelector('#AuraBusinessName'),
        rut: this.querySelector('#AuraBusinessRut'),
        activity: this.querySelector('#AuraBusinessActivity'),
        email: this.querySelector('#AuraBillingEmail'),
      };
      this.saveQueue = Promise.resolve();
      this.lastSaved = '';
      this.checkoutBypass = false;

      this.radios.forEach((radio) => radio.addEventListener('change', () => this.handleDocumentChange()));
      Object.values(this.fields).forEach((field) => {
        field.addEventListener('blur', () => this.handleFieldBlur(field));
        field.addEventListener('input', () => this.clearFieldError(field));
      });
      this.checkoutButton?.addEventListener('click', (event) => this.handleCheckout(event));
      window.addEventListener('resize', this.updateStickyStateBound = () => this.updateStickyState());

      this.updateVisibility(false);
      requestAnimationFrame(() => this.updateStickyState());

      if (this.dataset.documentSaved !== 'true') {
        this.queueSave(this.buildAttributes(), true).catch(() => {
          this.showSaveError('No pudimos guardar tu selección. Inténtalo nuevamente antes de continuar.');
        });
      }
    }

    disconnectedCallback() {
      if (this.updateStickyStateBound) window.removeEventListener('resize', this.updateStickyStateBound);
    }

    get documentType() {
      return this.radios.find((radio) => radio.checked)?.value || 'Boleta';
    }

    get isInvoice() {
      return this.documentType === 'Factura empresa';
    }

    handleDocumentChange() {
      this.clearAllErrors();
      this.updateVisibility(true);
      this.queueSave(this.buildAttributes(), true).catch(() => {
        this.showSaveError('No pudimos guardar tus datos. Inténtalo nuevamente antes de continuar.');
      });
    }

    handleFieldBlur(field) {
      if (!this.isInvoice) return;
      if (field === this.fields.rut && field.value.trim()) field.value = this.formatRut(field.value);
      this.validateField(field);
      this.queueSave(this.buildAttributes()).catch(() => {
        this.showSaveError('No pudimos guardar tus datos. Inténtalo nuevamente antes de continuar.');
      });
    }

    updateVisibility(animate) {
      const expanded = this.isInvoice;
      this.invoiceOption?.setAttribute('aria-expanded', String(expanded));
      this.invoicePanel?.setAttribute('aria-hidden', String(!expanded));
      this.invoicePanel?.classList.toggle('is-collapsed', !expanded);
      Object.values(this.fields).forEach((field) => { field.disabled = !expanded; });
      if (animate) this.invoicePanel?.classList.add('is-transitioning');
      window.setTimeout(() => {
        this.invoicePanel?.classList.remove('is-transitioning');
        this.updateStickyState();
      }, 320);
    }

    buildAttributes() {
      const attributes = { 'Documento tributario': this.documentType };
      if (this.isInvoice) {
        attributes['Razón social'] = this.fields.businessName.value.trim();
        attributes['RUT empresa'] = this.formatRut(this.fields.rut.value);
        attributes.Giro = this.fields.activity.value.trim();
        attributes['Correo de facturación'] = this.fields.email.value.trim();
      } else {
        attributes['Razón social'] = '';
        attributes['RUT empresa'] = '';
        attributes.Giro = '';
        attributes['Correo de facturación'] = '';
      }
      return attributes;
    }

    queueSave(attributes, force = false) {
      const signature = JSON.stringify(attributes);
      if (!force && signature === this.lastSaved) return this.saveQueue;
      this.saveQueue = this.saveQueue.catch(() => undefined).then(async () => {
        const response = await fetch(this.updateUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ attributes }),
        });
        if (!response.ok) throw new Error(`Cart update failed: ${response.status}`);
        await response.json();
        this.lastSaved = signature;
        this.showSaveError('');
      });
      return this.saveQueue;
    }

    async handleCheckout(event) {
      if (this.checkoutBypass) return;
      event.preventDefault();
      event.stopImmediatePropagation();

      if (!this.validate()) return;

      const originalLabel = this.checkoutButton.textContent;
      this.checkoutButton.disabled = true;
      this.checkoutButton.setAttribute('aria-busy', 'true');
      this.checkoutButton.textContent = 'Guardando…';
      this.showSaveError('');

      try {
        await this.queueSave(this.buildAttributes(), true);
        this.checkoutBypass = true;
        this.checkoutButton.disabled = false;
        this.checkoutButton.removeAttribute('aria-busy');
        this.checkoutButton.textContent = originalLabel;
        this.checkoutButton.click();
      } catch (error) {
        this.checkoutButton.disabled = false;
        this.checkoutButton.removeAttribute('aria-busy');
        this.checkoutButton.textContent = originalLabel;
        this.showSaveError('No pudimos guardar tus datos. Inténtalo nuevamente antes de continuar.');
      }
    }

    validate() {
      if (!this.isInvoice) {
        this.clearAllErrors();
        return true;
      }
      const invalid = Object.values(this.fields).filter((field) => !this.validateField(field));
      this.updateStickyState();
      if (invalid.length) invalid[0].focus({ preventScroll: false });
      return invalid.length === 0;
    }

    validateField(field) {
      let message = '';
      const value = field.value.trim();
      if (!value) message = 'Este campo es obligatorio.';
      else if (field === this.fields.rut && !this.isValidRut(value)) message = 'Ingresa un RUT chileno válido.';
      else if (field === this.fields.email && !field.validity.valid) message = 'Ingresa un correo electrónico válido.';
      this.setFieldError(field, message);
      return !message;
    }

    setFieldError(field, message) {
      const error = this.querySelector(`[data-error-for="${field.id}"]`);
      field.setAttribute('aria-invalid', String(Boolean(message)));
      if (message) field.setAttribute('aria-describedby', error.id || `${field.id}-error`);
      else field.removeAttribute('aria-describedby');
      if (error) {
        if (!error.id) error.id = `${field.id}-error`;
        error.textContent = message;
      }
    }

    clearFieldError(field) {
      this.setFieldError(field, '');
      this.showSaveError('');
    }

    clearAllErrors() {
      Object.values(this.fields).forEach((field) => this.setFieldError(field, ''));
      this.showSaveError('');
    }

    showSaveError(message) {
      if (this.saveError) this.saveError.textContent = message;
    }

    normalizeRut(value) {
      return value.toUpperCase().replace(/[^0-9K]/g, '');
    }

    isValidRut(value) {
      const rut = this.normalizeRut(value);
      if (!/^\d{7,8}[0-9K]$/.test(rut)) return false;
      const body = rut.slice(0, -1);
      const verifier = rut.slice(-1);
      let sum = 0;
      let multiplier = 2;
      for (let index = body.length - 1; index >= 0; index -= 1) {
        sum += Number(body[index]) * multiplier;
        multiplier = multiplier === 7 ? 2 : multiplier + 1;
      }
      const result = 11 - (sum % 11);
      const expected = result === 11 ? '0' : result === 10 ? 'K' : String(result);
      return verifier === expected;
    }

    formatRut(value) {
      const rut = this.normalizeRut(value);
      if (rut.length < 2) return value.trim();
      const verifier = rut.slice(-1);
      const body = rut.slice(0, -1).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
      return `${body}-${verifier}`;
    }

    updateStickyState() {
      const footer = this.closest('.aura-cart-summary-wrapper')?.querySelector('.cart__footer');
      if (!footer) return;
      const isDesktop = window.matchMedia('(min-width: 990px)').matches;
      const available = window.innerHeight - 60;
      footer.classList.toggle('aura-cart-summary--flow', !isDesktop || footer.scrollHeight > available);
    }
  }

  customElements.define('aura-tax-document', AuraTaxDocument);
}
